/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListWorkloadTokenEndpointsRequest,
  ListWorkloadTokenEndpointsSecurity,
} from "../models/operations/listworkloadtokenendpoints.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildWorkloadTokenEndpointsQuery,
  prefetchWorkloadTokenEndpoints,
  queryKeyWorkloadTokenEndpoints,
  WorkloadTokenEndpointsQueryData,
} from "./workloadTokenEndpoints.core.js";
export {
  buildWorkloadTokenEndpointsQuery,
  prefetchWorkloadTokenEndpoints,
  queryKeyWorkloadTokenEndpoints,
  type WorkloadTokenEndpointsQueryData,
};

export type WorkloadTokenEndpointsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listTokenEndpoints workloadIdentities
 *
 * @remarks
 * List the token endpoints an external platform can be pointed at: one per user session issuer in shared mode, at the organization level and in each project, with the issuer it serves. Issuers this deployment does not serve a shared authorization server for are left out. Requires workload:read.
 */
export function useWorkloadTokenEndpoints(
  request?: ListWorkloadTokenEndpointsRequest | undefined,
  security?: ListWorkloadTokenEndpointsSecurity | undefined,
  options?: QueryHookOptions<
    WorkloadTokenEndpointsQueryData,
    WorkloadTokenEndpointsQueryError
  >,
): UseQueryResult<
  WorkloadTokenEndpointsQueryData,
  WorkloadTokenEndpointsQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildWorkloadTokenEndpointsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listTokenEndpoints workloadIdentities
 *
 * @remarks
 * List the token endpoints an external platform can be pointed at: one per user session issuer in shared mode, at the organization level and in each project, with the issuer it serves. Issuers this deployment does not serve a shared authorization server for are left out. Requires workload:read.
 */
export function useWorkloadTokenEndpointsSuspense(
  request?: ListWorkloadTokenEndpointsRequest | undefined,
  security?: ListWorkloadTokenEndpointsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    WorkloadTokenEndpointsQueryData,
    WorkloadTokenEndpointsQueryError
  >,
): UseSuspenseQueryResult<
  WorkloadTokenEndpointsQueryData,
  WorkloadTokenEndpointsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildWorkloadTokenEndpointsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setWorkloadTokenEndpointsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: WorkloadTokenEndpointsQueryData,
): WorkloadTokenEndpointsQueryData | undefined {
  const key = queryKeyWorkloadTokenEndpoints(...queryKeyBase);

  return client.setQueryData<WorkloadTokenEndpointsQueryData>(key, data);
}

export function invalidateWorkloadTokenEndpoints(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "workloadIdentities",
      "listTokenEndpoints",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllWorkloadTokenEndpoints(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "workloadIdentities", "listTokenEndpoints"],
  });
}
